export interface Festival {
  id: string;
  name: string;
  date: string;
  month: string;
  description: string;
  significance: string;
  rituals: string[];
  image: string;
  category: string;
  detailedStory: string;
  detailedRituals: Array<{
    title: string;
    description: string;
  }>;
  spiritualBenefits: string[];
  pujaItems: string[];
  mantras: Array<{
    title: string;
    sanskrit: string;
    meaning: string;
  }>;
  bestTime: string;
  celebratedIn: string;
  deity: string;
}

export const festivalsData: Festival[] = [
  {
    id: 'maha-shivaratri',
    name: 'Maha Shivaratri',
    date: '26',
    month: 'February 2025',
    description: 'The great night of Lord Shiva, observed with fasting, night vigil, and worship.',
    significance: 'Commemorates the marriage of Shiva and Parvati, and the night when Shiva performed the Tandava dance.',
    rituals: ['Fasting', 'Night Vigil', 'Abhishekam', 'Chanting Om Namah Shivaya'],
    image: 'https://public.readdy.ai/ai/img_res/bfcb03acf545e397c867a04fa95e620d.jpg',
    category: 'major',
    detailedStory: 'Maha Shivaratri, meaning "The Great Night of Shiva," is one of the most significant festivals in Hinduism. According to ancient scriptures, this is the night when Lord Shiva performed the cosmic dance of creation, preservation, and destruction - the Tandava. It is also believed to be the night when Shiva and Parvati were married, symbolizing the union of consciousness and energy. Another legend states that on this night, Shiva saved the world by consuming the poison that emerged from the ocean during Samudra Manthan, turning his throat blue and earning him the name Neelkanth. Devotees observe this sacred night with complete devotion, staying awake and worshipping Shiva to receive his divine blessings and liberation from the cycle of birth and death.',
    detailedRituals: [
      {
        title: 'Fasting (Vrat)',
        description: 'Devotees observe a strict fast throughout the day and night, consuming only fruits, milk, and water. Some observe a complete nirjala fast without even water. The fast is broken only the next morning after the puja.'
      },
      {
        title: 'Abhishekam',
        description: 'The Shiva Lingam is bathed with milk, yogurt, honey, ghee, sugar, and water while chanting mantras. Each substance represents different aspects of purification and devotion. Bilva leaves are offered as they are especially dear to Lord Shiva.'
      },
      {
        title: 'Night Vigil (Jagaran)',
        description: 'Devotees stay awake throughout the night, singing bhajans, chanting mantras, and meditating on Lord Shiva. The night is divided into four quarters, with special pujas performed during each quarter.'
      },
      {
        title: 'Rudra Abhishekam',
        description: 'Special elaborate abhishekam performed with 11 or 21 different sacred items including sandalwood paste, rose water, and sacred ash. This powerful ritual is believed to remove negative karma and bestow divine grace.'
      }
    ],
    spiritualBenefits: [
      'Liberation from the cycle of birth and death',
      'Removal of past sins and negative karma',
      'Attainment of spiritual wisdom and enlightenment',
      'Fulfillment of desires and wishes',
      'Protection from negative energies',
      'Inner peace and mental clarity',
      'Strengthening of devotion and faith'
    ],
    pujaItems: [
      'Shiva Lingam or Picture',
      'Bilva Leaves (Bel Patra)',
      'Milk and Yogurt',
      'Honey and Ghee',
      'Gangajal (Holy Water)',
      'White Flowers',
      'Rudraksha Mala',
      'Incense and Camphor',
      'Dhatura Flowers',
      'Sandalwood Paste',
      'Sacred Ash (Vibhuti)',
      'Copper Kalash'
    ],
    mantras: [
      {
        title: 'Maha Mrityunjaya Mantra',
        sanskrit: 'ॐ त्र्यम्बकं यजामहे सुगन्धिं पुष्टिवर्धनम् उर्वारुकमिव बन्धनान्मृत्योर्मुक्षीय माऽमृतात्',
        meaning: 'We worship the three-eyed one (Lord Shiva) who is fragrant and nourishes all beings. May he liberate us from death for the sake of immortality, just as the ripe cucumber is severed from its bondage to the vine.'
      },
      {
        title: 'Shiva Panchakshari Mantra',
        sanskrit: 'ॐ नमः शिवाय',
        meaning: 'I bow to Lord Shiva. This five-syllable mantra is the most powerful and sacred mantra dedicated to Lord Shiva, representing the five elements and the divine consciousness.'
      }
    ],
    bestTime: 'Night (Nishita Kaal)',
    celebratedIn: 'All over India',
    deity: 'Lord Shiva'
  },
  {
    id: 'holi',
    name: 'Holi',
    date: '14',
    month: 'March 2025',
    description: 'Festival of colors celebrating the victory of good over evil and the arrival of spring.',
    significance: 'Celebrates the divine love of Radha and Krishna, and the burning of Holika.',
    rituals: ['Holika Dahan', 'Playing with Colors', 'Singing and Dancing', 'Special Sweets'],
    image: 'https://readdy.ai/api/search-image?query=vibrant%20Holi%20celebration%20with%20colorful%20powder%20gulal%20in%20brass%20bowls%2C%20traditional%20setup%20with%20marigold%20flowers%2C%20natural%20organic%20colors%20red%20yellow%20green%20blue%2C%20festive%20atmosphere%2C%20warm%20sunlight%2C%20cultural%20celebration%20elements%2C%20joyful%20spring%20festival%20mood%2C%20traditional%20Indian%20setting&width=600&height=400&seq=fest002&orientation=landscape',
    category: 'major',
    detailedStory: 'Holi, the festival of colors, celebrates the eternal love between Lord Krishna and Radha, and marks the victory of good over evil through the story of Prahlad and Holika. According to legend, the demon king Hiranyakashipu wanted everyone to worship him, but his son Prahlad remained devoted to Lord Vishnu. Enraged, the king asked his sister Holika, who was immune to fire, to sit in a pyre with Prahlad. However, due to divine intervention, Holika burned while Prahlad emerged unharmed. The night before Holi, bonfires are lit to commemorate this event. The next day, people celebrate with colors, remembering how Krishna playfully colored Radha and the gopis. This joyous festival signifies the arrival of spring, the blossoming of love, and the triumph of devotion over evil.',
    detailedRituals: [
      {
        title: 'Holika Dahan',
        description: 'On the eve of Holi, a bonfire is lit in the evening to symbolize the burning of evil and negativity. People gather around the fire, perform puja, and offer prayers. Coconuts and grains are offered to the fire as a symbol of gratitude for a good harvest.'
      },
      {
        title: 'Playing with Colors (Rangwali Holi)',
        description: 'The main day of Holi is celebrated by playing with vibrant colored powders (gulal) and water. People smear colors on each other, dance to traditional songs, and spread joy. This represents the colorful love of Radha and Krishna.'
      },
      {
        title: 'Traditional Music and Dance',
        description: 'Folk songs, dhol beats, and traditional dances like the Raas Leela are performed. People sing Holi songs and bhajans dedicated to Lord Krishna, creating a festive and devotional atmosphere.'
      },
      {
        title: 'Festive Feast',
        description: 'Special delicacies like gujiya, malpua, thandai, and other sweets are prepared and shared with family and friends. The traditional drink thandai, often infused with bhang, is consumed as part of the celebration.'
      }
    ],
    spiritualBenefits: [
      'Renewal of relationships and forgiveness',
      'Cleansing of past negativity',
      'Celebration of divine love',
      'Welcoming positive energy with spring',
      'Strengthening community bonds',
      'Joy and spiritual upliftment',
      'Remembrance of Krishna\'s divine play'
    ],
    pujaItems: [
      'Holika Wood and Cow Dung Cakes',
      'Coconut',
      'Wheat Grains',
      'Natural Color Powders (Gulal)',
      'Flowers',
      'Incense Sticks',
      'Camphor',
      'Sweets (Gujiya, Malpua)',
      'Thandai Ingredients',
      'Puja Thali',
      'Water for Abhishekam',
      'Kumkum and Turmeric'
    ],
    mantras: [
      {
        title: 'Krishna Mantra',
        sanskrit: 'ॐ श्री कृष्णाय नमः',
        meaning: 'I bow to Lord Krishna. This mantra invokes the blessings of Lord Krishna and celebrates his divine love and playfulness that Holi represents.'
      },
      {
        title: 'Holika Dahan Mantra',
        sanskrit: 'असत्यमपि सत्यं स्यात् सत्यमप्यसतं भवेत्',
        meaning: 'May falsehood become truth and truth overcome falsehood. This mantra is chanted during Holika Dahan to invoke the victory of truth and righteousness.'
      }
    ],
    bestTime: 'Morning (after sunrise)',
    celebratedIn: 'All over India, especially North India',
    deity: 'Lord Krishna and Radha'
  },
  {
    id: 'ram-navami',
    name: 'Ram Navami',
    date: '6',
    month: 'April 2025',
    description: 'Celebrates the birth of Lord Rama, the seventh avatar of Vishnu.',
    significance: 'Marks the birth of Lord Rama in Ayodhya, symbolizing the victory of dharma.',
    rituals: ['Ramayana Recitation', 'Bhajans', 'Fasting', 'Temple Visits'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Lord%20Rama%20deity%20with%20bow%20and%20arrow%2C%20adorned%20with%20fresh%20flower%20garlands%2C%20traditional%20temple%20decoration%2C%20golden%20crown%2C%20sacred%20atmosphere%2C%20devotional%20setting%2C%20warm%20lighting%2C%20spiritual%20reverence%2C%20traditional%20Hindu%20iconography%2C%20peaceful%20divine%20presence&width=600&height=400&seq=fest003&orientation=landscape',
    category: 'major',
    detailedStory: 'Ram Navami celebrates the birth of Lord Rama, the seventh incarnation of Lord Vishnu, who descended to Earth to destroy evil and establish dharma. Born to King Dasharatha and Queen Kaushalya in Ayodhya on the ninth day of Chaitra month, Rama\'s life exemplifies the perfect son, brother, husband, and king. His story, chronicled in the epic Ramayana, teaches humanity about righteousness, duty, devotion, and moral values. Rama\'s unwavering commitment to truth and dharma, even in the face of great personal sacrifice, makes him the ideal man (Maryada Purushottam). On this auspicious day, devotees celebrate his divine birth by reading the Ramayana, singing bhajans, fasting, and visiting temples. The festival reminds us to follow the path of righteousness and uphold moral values in our lives.',
    detailedRituals: [
      {
        title: 'Ramayana Path',
        description: 'Devotees gather to recite and listen to the complete Ramayana or selected chapters, especially the Sundara Kanda. The recitation begins nine days before Ram Navami and culminates on the birth day with the reading of the birth chapter (Bal Kanda).'
      },
      {
        title: 'Rama Kalyanam',
        description: 'In South India, the ceremonial wedding of Rama and Sita is enacted in temples with great devotion. Idols are beautifully decorated, and the divine marriage ceremony is performed with Vedic rituals.'
      },
      {
        title: 'Fasting and Prayer',
        description: 'Devotees observe a fast from sunrise to sunset or consume only fruits and milk. The fast is broken after the midday puja when Rama is believed to have been born. Special prayers and bhajans are sung throughout the day.'
      },
      {
        title: 'Shobha Yatra',
        description: 'Grand processions are taken out with decorated idols of Rama, Sita, Lakshmana, and Hanuman. Devotees sing devotional songs and chant "Jai Shri Ram" as the procession moves through the streets.'
      }
    ],
    spiritualBenefits: [
      'Cultivation of dharma and righteousness',
      'Development of moral character',
      'Removal of obstacles and difficulties',
      'Protection from negative influences',
      'Strengthening of family bonds',
      'Attainment of peace and prosperity',
      'Inspiration to follow the path of truth'
    ],
    pujaItems: [
      'Rama Idol or Picture',
      'Fresh Flowers (especially Tulsi)',
      'Fruits and Sweets',
      'Incense and Camphor',
      'Sandalwood Paste',
      'Kumkum and Turmeric',
      'Betel Leaves and Nuts',
      'Coconut',
      'Panchamrit',
      'Tulsi Leaves',
      'Diya and Oil',
      'Ramayana Book'
    ],
    mantras: [
      {
        title: 'Rama Mantra',
        sanskrit: 'ॐ श्री रामाय नमः',
        meaning: 'I bow to Lord Rama. This simple yet powerful mantra invokes the blessings of Lord Rama and helps devotees connect with his divine energy.'
      },
      {
        title: 'Rama Gayatri Mantra',
        sanskrit: 'ॐ दाशरथये विद्महे सीतावल्लभाय धीमहि तन्नो रामः प्रचोदयात्',
        meaning: 'We meditate on the son of Dasharatha, the beloved of Sita. May Lord Rama illuminate our intellect and guide us on the righteous path.'
      }
    ],
    bestTime: 'Midday (Madhyahna)',
    celebratedIn: 'All over India, especially Ayodhya',
    deity: 'Lord Rama'
  },
  {
    id: 'hanuman-jayanti',
    name: 'Hanuman Jayanti',
    date: '13',
    month: 'April 2025',
    description: 'Celebrates the birth of Lord Hanuman, the devoted follower of Lord Rama.',
    significance: 'Honors Hanuman\'s strength, devotion, and selfless service.',
    rituals: ['Hanuman Chalisa', 'Special Puja', 'Distribution of Prasad', 'Reading Ramayana'],
    image: 'https://readdy.ai/api/search-image?query=powerful%20Hanuman%20deity%20statue%20in%20vermillion%20sindoor%2C%20holding%20gada%20mace%2C%20devotional%20pose%2C%20temple%20setting%20with%20marigold%20garlands%2C%20oil%20lamps%2C%20traditional%20worship%20setup%2C%20spiritual%20strength%2C%20warm%20golden%20lighting%2C%20sacred%20atmosphere%2C%20traditional%20Hindu%20iconography&width=600&height=400&seq=fest004&orientation=landscape',
    category: 'major',
    detailedStory: 'Hanuman Jayanti celebrates the birth of Lord Hanuman, the epitome of devotion, strength, and selfless service. Born to Anjana and Kesari with the blessings of Lord Vayu (the wind god), Hanuman is considered an incarnation of Lord Shiva. From childhood, Hanuman displayed extraordinary powers - he once leaped toward the sun thinking it was a fruit. His unwavering devotion to Lord Rama is legendary. During the search for Sita, Hanuman crossed the ocean to Lanka, found Sita, and later brought the Sanjeevani herb to save Lakshmana\'s life. His strength, wisdom, and complete surrender to Rama make him the ideal devotee. Hanuman is worshipped for courage, strength, protection from evil, and the removal of obstacles. His devotees believe that sincere worship of Hanuman brings success, good health, and spiritual progress.',
    detailedRituals: [
      {
        title: 'Hanuman Chalisa Recitation',
        description: 'The 40-verse prayer composed by Tulsidas is recited multiple times throughout the day. Many devotees recite it 11, 21, or 108 times. The Chalisa praises Hanuman\'s virtues and seeks his blessings for strength and protection.'
      },
      {
        title: 'Sindoor Offering',
        description: 'Devotees apply vermillion (sindoor) to Hanuman\'s idol as it is believed to be his favorite offering. According to legend, when Sita applied sindoor for Rama\'s long life, Hanuman covered his entire body with it to ensure Rama\'s immortality.'
      },
      {
        title: 'Special Abhishekam',
        description: 'The idol is bathed with panchamrit (mixture of milk, yogurt, honey, ghee, and sugar), followed by water and sandalwood paste. Flowers, especially red flowers and marigolds, are offered in abundance.'
      },
      {
        title: 'Prasad Distribution',
        description: 'Special prasad including besan laddoos, bananas, and other sweets are prepared and distributed. It is believed that Hanuman loves besan laddoos, and offering them brings his blessings.'
      }
    ],
    spiritualBenefits: [
      'Increased physical and mental strength',
      'Protection from negative energies and evil spirits',
      'Removal of obstacles and difficulties',
      'Development of courage and confidence',
      'Deepening of devotion and faith',
      'Success in endeavors',
      'Good health and vitality'
    ],
    pujaItems: [
      'Hanuman Idol or Picture',
      'Vermillion (Sindoor)',
      'Red Flowers',
      'Besan Laddoos',
      'Bananas',
      'Coconut',
      'Incense and Camphor',
      'Sandalwood Paste',
      'Jasmine Oil',
      'Red Cloth',
      'Hanuman Chalisa Book',
      'Diya and Ghee'
    ],
    mantras: [
      {
        title: 'Hanuman Mantra',
        sanskrit: 'ॐ हं हनुमते नमः',
        meaning: 'I bow to Lord Hanuman. This powerful beej mantra invokes Hanuman\'s energy and provides strength, courage, and protection to the devotee.'
      },
      {
        title: 'Hanuman Gayatri Mantra',
        sanskrit: 'ॐ अञ्जनेयाय विद्महे वायुपुत्राय धीमहि तन्नो हनुमत् प्रचोदयात्',
        meaning: 'We meditate on the son of Anjana, the son of the wind god. May Lord Hanuman illuminate our intellect and inspire us with devotion and strength.'
      }
    ],
    bestTime: 'Morning (Sunrise)',
    celebratedIn: 'All over India',
    deity: 'Lord Hanuman'
  },
  {
    id: 'akshaya-tritiya',
    name: 'Akshaya Tritiya',
    date: '30',
    month: 'April 2025',
    description: 'Auspicious day for new beginnings, considered highly favorable for important ventures.',
    significance: 'Believed to bring prosperity and success, marks the beginning of Treta Yuga.',
    rituals: ['Lakshmi Puja', 'Charity', 'Gold Purchase', 'New Ventures'],
    image: 'https://readdy.ai/api/search-image?query=auspicious%20Lakshmi%20puja%20setup%20with%20gold%20coins%2C%20yellow%20flowers%2C%20brass%20lamps%2C%20turmeric%2C%20rice%20grains%2C%20prosperity%20symbols%2C%20traditional%20wealth%20worship%2C%20warm%20golden%20lighting%2C%20sacred%20atmosphere%2C%20festive%20decorations%2C%20spiritual%20abundance%20theme&width=600&height=400&seq=fest005&orientation=landscape',
    category: 'auspicious',
    detailedStory: 'Akshaya Tritiya, falling on the third lunar day of the bright half of Vaishakha month, is considered one of the most auspicious days in the Hindu calendar. "Akshaya" means eternal or never diminishing, and anything begun on this day is believed to bring lasting prosperity and success. According to mythology, this is the day when Treta Yuga began, when Lord Parashurama (the sixth avatar of Vishnu) was born, and when Ved Vyasa and Lord Ganesha began writing the Mahabharata and Puranas. It is also believed that on this day, the poor Brahmin Sudama visited his childhood friend Lord Krishna with a humble offering of poha (flattened rice), and Krishna blessed him with immense wealth. The day is considered so auspicious that no muhurat is needed for any new venture. People buy gold, start new businesses, perform weddings, and make charitable donations on this day.',
    detailedRituals: [
      {
        title: 'Lakshmi-Narayana Puja',
        description: 'Special worship of Goddess Lakshmi and Lord Vishnu is performed to invoke blessings of wealth and prosperity. The puja includes offering yellow flowers, turmeric, and gold ornaments to the deities.'
      },
      {
        title: 'Charity and Dana',
        description: 'Giving charity on this day is considered highly meritorious. Devotees donate food, clothes, money, and gold to the needy. Feeding Brahmins and the poor is especially auspicious and brings eternal merit.'
      },
      {
        title: 'Gold Purchase',
        description: 'Buying gold on Akshaya Tritiya is a popular tradition as it symbolizes eternal prosperity. Even a small gold coin or jewelry is purchased to invite Lakshmi\'s blessings into the home.'
      },
      {
        title: 'Starting New Ventures',
        description: 'This is an ideal day to begin new businesses, investments, construction of homes, or any important undertaking. Marriages performed on this day are believed to be blessed with eternal happiness.'
      }
    ],
    spiritualBenefits: [
      'Eternal prosperity and wealth',
      'Success in new ventures',
      'Removal of financial obstacles',
      'Accumulation of merit through charity',
      'Blessings of Goddess Lakshmi',
      'Long-lasting positive results',
      'Spiritual and material abundance'
    ],
    pujaItems: [
      'Lakshmi and Vishnu Idols',
      'Gold Coin or Jewelry',
      'Yellow Flowers',
      'Turmeric and Kumkum',
      'Rice Grains',
      'Coconut',
      'Fruits and Sweets',
      'Incense and Camphor',
      'Betel Leaves and Nuts',
      'New Clothes',
      'Diya and Ghee',
      'Donation Items'
    ],
    mantras: [
      {
        title: 'Lakshmi Mantra',
        sanskrit: 'ॐ श्रीं ह्रीं श्रीं कमले कमलालये प्रसीद प्रसीद ॐ श्रीं ह्रीं श्रीं महालक्ष्म्यै नमः',
        meaning: 'I bow to Goddess Mahalakshmi who resides in the lotus. May she be pleased and bestow her blessings of prosperity, abundance, and spiritual wealth upon us.'
      },
      {
        title: 'Vishnu Mantra',
        sanskrit: 'ॐ नमो भगवते वासुदेवाय',
        meaning: 'I bow to Lord Vasudeva (Vishnu). This mantra invokes the blessings of Lord Vishnu for protection, prosperity, and spiritual growth.'
      }
    ],
    bestTime: 'Morning (Brahma Muhurta to Noon)',
    celebratedIn: 'All over India',
    deity: 'Goddess Lakshmi and Lord Vishnu'
  },
  {
    id: 'guru-purnima',
    name: 'Guru Purnima',
    date: '13',
    month: 'July 2025',
    description: 'Day dedicated to honoring spiritual and academic teachers.',
    significance: 'Celebrates the guru-disciple tradition and expresses gratitude to teachers.',
    rituals: ['Guru Puja', 'Pada Puja', 'Offering Dakshina', 'Spiritual Discourses'],
    image: 'https://readdy.ai/api/search-image?query=serene%20guru%20worship%20setup%20with%20feet%20washing%20ceremony%2C%20flowers%2C%20incense%2C%20traditional%20guru%20pada%20puja%20elements%2C%20spiritual%20teacher%20reverence%2C%20peaceful%20atmosphere%2C%20warm%20lighting%2C%20devotional%20mood%2C%20traditional%20Indian%20spiritual%20setting%2C%20sacred%20respect&width=600&height=400&seq=fest006&orientation=landscape',
    category: 'spiritual',
    detailedStory: 'Guru Purnima, celebrated on the full moon day of Ashadha month, is dedicated to honoring the guru (spiritual teacher) who dispels the darkness of ignorance and leads disciples toward enlightenment. The word "guru" comes from "gu" (darkness) and "ru" (remover). This day commemorates the birth of Ved Vyasa, the sage who compiled the Vedas and authored the Mahabharata and Puranas, making him the Adi Guru (first teacher) of Hindu tradition. It is also believed that on this day, Lord Shiva became the Adi Yogi and began teaching yoga to the Saptarishis. The guru-disciple relationship is considered sacred in Indian culture, as the guru not only imparts knowledge but also guides the disciple\'s spiritual evolution. On this day, disciples express gratitude, seek blessings, and renew their commitment to learning and spiritual practice.',
    detailedRituals: [
      {
        title: 'Guru Puja',
        description: 'Disciples perform puja to their guru by offering flowers, fruits, and incense. If the guru is not physically present, their photo or sandals (paduka) are worshipped with devotion and reverence.'
      },
      {
        title: 'Pada Puja (Feet Washing)',
        description: 'The traditional ritual of washing the guru\'s feet with water, milk, and flowers is performed as a gesture of humility and surrender. This symbolizes the disciple\'s readiness to serve and learn.'
      },
      {
        title: 'Offering Dakshina',
        description: 'Disciples offer dakshina (monetary offering) or gifts to the guru as a token of gratitude. The offering represents the disciple\'s acknowledgment of the invaluable knowledge received.'
      },
      {
        title: 'Spiritual Discourses and Meditation',
        description: 'The day is spent in spiritual activities like listening to the guru\'s teachings, group meditation, chanting, and studying sacred texts. Many ashrams organize special satsangs and discourses.'
      }
    ],
    spiritualBenefits: [
      'Deepening of spiritual knowledge',
      'Strengthening guru-disciple bond',
      'Removal of ignorance',
      'Acceleration of spiritual progress',
      'Development of humility and devotion',
      'Clarity of purpose and direction',
      'Blessings for success in learning'
    ],
    pujaItems: [
      'Guru\'s Photo or Paduka',
      'Fresh Flowers',
      'Fruits',
      'Incense and Camphor',
      'Sandalwood Paste',
      'Kumkum and Turmeric',
      'Coconut',
      'Sweets',
      'New Cloth',
      'Dakshina (Money)',
      'Sacred Books',
      'Diya and Ghee'
    ],
    mantras: [
      {
        title: 'Guru Mantra',
        sanskrit: 'गुरुर्ब्रह्मा गुरुर्विष्णुः गुरुर्देवो महेश्वरः। गुरुः साक्षात् परं ब्रह्म तस्मै श्री गुरवे नमः॥',
        meaning: 'The Guru is Brahma, Vishnu, and Shiva. The Guru is verily the Supreme Absolute. I offer my salutations to that revered Guru.'
      },
      {
        title: 'Guru Gayatri Mantra',
        sanskrit: 'ॐ गुरुदेवाय विद्महे परब्रह्मणे धीमहि तन्नो गुरुः प्रचोदयात्',
        meaning: 'We meditate on the divine Guru, the embodiment of supreme consciousness. May the Guru illuminate our intellect and guide us toward truth.'
      }
    ],
    bestTime: 'Morning and Evening',
    celebratedIn: 'All over India',
    deity: 'Guru (Spiritual Teacher)'
  },
  {
    id: 'raksha-bandhan',
    name: 'Raksha Bandhan',
    date: '9',
    month: 'August 2025',
    description: 'Celebrates the bond between brothers and sisters.',
    significance: 'Sisters tie rakhi on brothers\' wrists, symbolizing protection and love.',
    rituals: ['Tying Rakhi', 'Aarti', 'Exchange of Gifts', 'Sweet Distribution'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20traditional%20rakhi%20threads%20with%20silk%2C%20beads%2C%20decorative%20elements%20on%20brass%20thali%20plate%2C%20kumkum%2C%20rice%20grains%2C%20sweets%2C%20marigold%20flowers%2C%20festive%20family%20celebration%20setup%2C%20warm%20lighting%2C%20cultural%20bond%20celebration%2C%20traditional%20Indian%20festival&width=600&height=400&seq=fest007&orientation=landscape',
    category: 'family',
    detailedStory: 'Raksha Bandhan, meaning "the bond of protection," celebrates the sacred relationship between brothers and sisters. The festival has its roots in several ancient legends. One story tells of Goddess Lakshmi observing a rakhi on King Bali to protect him. Another legend speaks of Draupadi tearing a piece of her sari to bandage Lord Krishna\'s bleeding finger, and in return, Krishna vowed to protect her always - a promise he fulfilled during her vastraharan. The festival also commemorates the story of Rani Karnavati of Mewar who sent a rakhi to Mughal Emperor Humayun seeking protection, and he honored the bond by coming to her aid. On this day, sisters tie a sacred thread (rakhi) on their brothers\' wrists, pray for their well-being, and brothers pledge to protect their sisters throughout their lives. The festival transcends blood relations and celebrates all protective bonds of love and care.',
    detailedRituals: [
      {
        title: 'Rakhi Tying Ceremony',
        description: 'Sisters prepare a puja thali with rakhi, kumkum, rice grains, diya, and sweets. They perform aarti, apply tilak on their brother\'s forehead, tie the rakhi on his wrist, and pray for his long life and prosperity.'
      },
      {
        title: 'Brother\'s Pledge',
        description: 'After receiving the rakhi, brothers give gifts to their sisters and promise to protect and support them throughout their lives. This exchange symbolizes the mutual love and responsibility in the sibling relationship.'
      },
      {
        title: 'Family Gathering',
        description: 'Families come together to celebrate the occasion with special meals and sweets. Traditional dishes and the sister\'s favorite foods are prepared to mark the celebration.'
      },
      {
        title: 'Sweet Exchange',
        description: 'Brothers and sisters exchange sweets and gifts. The sharing of sweets symbolizes the sweetness of their relationship and the joy of being together.'
      }
    ],
    spiritualBenefits: [
      'Strengthening of family bonds',
      'Protection and well-being',
      'Expression of love and gratitude',
      'Renewal of commitments',
      'Harmony in relationships',
      'Blessings for siblings',
      'Celebration of duty and care'
    ],
    pujaItems: [
      'Rakhi Threads',
      'Puja Thali',
      'Kumkum and Rice',
      'Diya and Oil',
      'Sweets',
      'Dry Fruits',
      'Flowers',
      'Incense Sticks',
      'Coconut',
      'Gifts',
      'New Clothes',
      'Money (Shagun)'
    ],
    mantras: [
      {
        title: 'Raksha Bandhan Mantra',
        sanskrit: 'येन बद्धो बलि राजा दानवेन्द्रो महाबलः। तेन त्वां प्रतिबध्नामि रक्षे मा चल मा चल॥',
        meaning: 'I tie this rakhi on you, the same way it was tied on the powerful demon king Bali. O Raksha, be firm, do not waver. This mantra is chanted while tying the rakhi.'
      }
    ],
    bestTime: 'Morning (after sunrise)',
    celebratedIn: 'All over India',
    deity: 'Sibling Bond'
  },
  {
    id: 'krishna-janmashtami',
    name: 'Krishna Janmashtami',
    date: '26',
    month: 'August 2025',
    description: 'Celebrates the birth of Lord Krishna, the eighth avatar of Vishnu.',
    significance: 'Marks the divine birth of Krishna at midnight in Mathura.',
    rituals: ['Midnight Celebration', 'Dahi Handi', 'Bhajans', 'Fasting until Midnight'],
    image: 'https://readdy.ai/api/search-image?query=adorable%20baby%20Krishna%20deity%20with%20peacock%20feather%20crown%2C%20butter%20pot%2C%20flute%2C%20decorated%20cradle%2C%20flower%20garlands%2C%20traditional%20janmashtami%20celebration%2C%20joyful%20devotional%20atmosphere%2C%20warm%20golden%20lighting%2C%20festive%20decorations%2C%20spiritual%20celebration%20mood&width=600&height=400&seq=fest008&orientation=landscape',
    category: 'major',
    detailedStory: 'Krishna Janmashtami celebrates the birth of Lord Krishna, the eighth avatar of Lord Vishnu, who incarnated to destroy evil and establish dharma. Born at midnight in a prison cell in Mathura to Devaki and Vasudeva, Krishna\'s birth was miraculous. His maternal uncle, the tyrant King Kansa, had imprisoned his parents after a prophecy declared that Devaki\'s eighth son would kill him. On the night of Krishna\'s birth, divine intervention helped Vasudeva carry the newborn across the Yamuna River to Gokul, where he was raised by Yashoda and Nanda. Krishna\'s childhood was filled with divine play (leelas) - from stealing butter to lifting Govardhan mountain, from dancing with the gopis to playing the enchanting flute. His teachings in the Bhagavad Gita provide timeless wisdom on duty, devotion, and the path to liberation. Janmashtami is celebrated with great devotion, fasting, midnight prayers, and joyous festivities.',
    detailedRituals: [
      {
        title: 'Fasting',
        description: 'Devotees observe a strict fast throughout the day, consuming only fruits and milk. The fast is broken only after midnight, at the auspicious moment of Krishna\'s birth, following the puja and aarti.'
      },
      {
        title: 'Midnight Celebration',
        description: 'Temples and homes are decorated beautifully. At midnight, the moment of Krishna\'s birth, special puja is performed. The idol of baby Krishna is placed in a decorated cradle, and devotees sing lullabies and bhajans.'
      },
      {
        title: 'Dahi Handi',
        description: 'In Maharashtra and other regions, the Dahi Handi ceremony is performed where a pot filled with curd, butter, and milk is hung high, and groups of young men form human pyramids to break it, recreating Krishna\'s childhood butter-stealing adventures.'
      },
      {
        title: 'Bhajan and Kirtan',
        description: 'Devotional songs, bhajans, and kirtans praising Krishna are sung throughout the day and night. The atmosphere is filled with devotion, joy, and celebration of Krishna\'s divine leelas.'
      }
    ],
    spiritualBenefits: [
      'Divine grace and blessings of Krishna',
      'Removal of sins and negative karma',
      'Spiritual joy and devotion',
      'Protection from evil',
      'Fulfillment of desires',
      'Inner peace and happiness',
      'Connection with divine love'
    ],
    pujaItems: [
      'Krishna Idol (Baby Krishna)',
      'Decorated Cradle',
      'Peacock Feather',
      'Flute',
      'Butter and Milk',
      'Tulsi Leaves',
      'Yellow Flowers',
      'Incense and Camphor',
      'Sandalwood Paste',
      'Fruits and Sweets',
      'Panchamrit',
      'Diya and Ghee'
    ],
    mantras: [
      {
        title: 'Krishna Mantra',
        sanskrit: 'ॐ श्री कृष्णाय नमः',
        meaning: 'I bow to Lord Krishna. This simple mantra invokes Krishna\'s divine presence and blessings.'
      },
      {
        title: 'Krishna Gayatri Mantra',
        sanskrit: 'ॐ देवकीनन्दनाय विद्महे वासुदेवाय धीमहि तन्नो कृष्णः प्रचोदयात्',
        meaning: 'We meditate on the son of Devaki, Lord Vasudeva. May Lord Krishna illuminate our intellect and guide us toward divine love and wisdom.'
      }
    ],
    bestTime: 'Midnight (Nishita Kaal)',
    celebratedIn: 'All over India, especially Mathura and Vrindavan',
    deity: 'Lord Krishna'
  },
  {
    id: 'ganesh-chaturthi',
    name: 'Ganesh Chaturthi',
    date: '27',
    month: 'August 2025',
    description: 'Celebrates the birth of Lord Ganesha, the remover of obstacles.',
    significance: 'Honors Ganesha as the god of wisdom, prosperity, and new beginnings.',
    rituals: ['Ganesha Installation', 'Daily Puja', 'Modak Offering', 'Visarjan'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Ganesha%20idol%20with%20elaborate%20decorations%2C%20modak%20sweets%2C%20red%20hibiscus%20flowers%2C%20durva%20grass%2C%20traditional%20puja%20setup%2C%20festive%20atmosphere%2C%20warm%20lighting%2C%20devotional%20celebration%2C%20colorful%20rangoli%2C%20spiritual%20joy%2C%20traditional%20Hindu%20festival&width=600&height=400&seq=fest009&orientation=landscape',
    category: 'major',
    detailedStory: 'Ganesh Chaturthi celebrates the birth of Lord Ganesha, the elephant-headed deity who is revered as the remover of obstacles, the patron of arts and sciences, and the god of intellect and wisdom. According to mythology, Goddess Parvati created Ganesha from sandalwood paste and breathed life into him to guard her while she bathed. When Lord Shiva returned and was stopped by Ganesha, an enraged Shiva beheaded him. To console the grieving Parvati, Shiva replaced Ganesha\'s head with that of an elephant and brought him back to life, declaring him the leader of all ganas (divine beings) and the first deity to be worshipped in any ritual. Ganesha\'s large elephant head symbolizes wisdom, his big ears represent the importance of listening, his small eyes signify concentration, and his large belly shows the ability to digest all good and bad in life. The festival is celebrated with great enthusiasm, especially in Maharashtra, with the installation of Ganesha idols in homes and public pandals.',
    detailedRituals: [
      {
        title: 'Ganesha Sthapana (Installation)',
        description: 'On the first day, a clay idol of Ganesha is brought home or installed in a pandal with Vedic rituals. The idol is invoked with mantras, and Ganesha is invited to reside in the idol for the duration of the festival.'
      },
      {
        title: 'Daily Puja',
        description: 'For 1.5, 3, 5, 7, or 11 days, devotees perform daily puja offering flowers, durva grass, modaks, and prayers. The puja includes 16 steps (Shodashopachara) of worship, aarti, and bhajans.'
      },
      {
        title: 'Modak Offering',
        description: 'Modak, a sweet dumpling, is Ganesha\'s favorite food. Devotees prepare 21 or 108 modaks and offer them to Ganesha. It is believed that offering modaks with devotion fulfills wishes and brings prosperity.'
      },
      {
        title: 'Visarjan (Immersion)',
        description: 'On the final day, the idol is taken in a grand procession with music and dancing to a water body for immersion. This ritual symbolizes Ganesha returning to his celestial abode, taking away all obstacles and negativity.'
      }
    ],
    spiritualBenefits: [
      'Removal of obstacles in life',
      'Blessings for new beginnings',
      'Wisdom and intellect',
      'Success in endeavors',
      'Prosperity and good fortune',
      'Protection from negative energies',
      'Spiritual growth and devotion'
    ],
    pujaItems: [
      'Ganesha Idol (Clay)',
      'Modak Sweets',
      'Durva Grass (21 blades)',
      'Red Hibiscus Flowers',
      'Coconut',
      'Betel Leaves and Nuts',
      'Fruits (especially Banana)',
      'Incense and Camphor',
      'Sandalwood Paste',
      'Kumkum and Turmeric',
      'Diya and Oil',
      'Rangoli Colors'
    ],
    mantras: [
      {
        title: 'Ganesha Mantra',
        sanskrit: 'ॐ गं गणपतये नमः',
        meaning: 'Salutations to Lord Ganesha. This powerful beej mantra removes obstacles and invokes Ganesha\'s blessings for success and prosperity.'
      },
      {
        title: 'Ganesha Gayatri Mantra',
        sanskrit: 'ॐ एकदन्ताय विद्महे वक्रतुण्डाय धीमहि तन्नो दन्ती प्रचोदयात्',
        meaning: 'We meditate on the one-tusked Lord, the one with the curved trunk. May Lord Ganesha illuminate our intellect and remove all obstacles from our path.'
      }
    ],
    bestTime: 'Morning and Evening',
    celebratedIn: 'All over India',
    deity: 'Lord Ganesha'
  },
  {
    id: 'navaratri',
    name: 'Navaratri',
    date: '2-11',
    month: 'October 2025',
    description: 'Nine nights dedicated to worshipping the Divine Mother in her various forms.',
    significance: 'Celebrates the victory of Goddess Durga over the demon Mahishasura.',
    rituals: ['Durga Puja', 'Garba Dance', 'Fasting', 'Kanya Puja'],
    image: 'https://readdy.ai/api/search-image?query=magnificent%20Goddess%20Durga%20idol%20with%20multiple%20arms%2C%20lion%20vahana%2C%20elaborate%20decorations%2C%20red%20and%20gold%20colors%2C%20traditional%20pandal%20setup%2C%20festive%20atmosphere%2C%20devotional%20celebration%2C%20warm%20lighting%2C%20spiritual%20power%2C%20traditional%20Hindu%20goddess%20worship&width=600&height=400&seq=fest010&orientation=landscape',
    category: 'major',
    detailedStory: 'Navaratri, meaning "nine nights," is one of the most significant Hindu festivals dedicated to worshipping the Divine Mother in her various forms. The festival celebrates the victory of Goddess Durga over the demon Mahishasura, symbolizing the triumph of good over evil. According to legend, Mahishasura had received a boon that no man or god could kill him, making him invincible. He terrorized the heavens and earth until the trinity of Brahma, Vishnu, and Shiva combined their powers to create Goddess Durga. Armed with weapons from all the gods and riding a lion, Durga fought Mahishasura for nine days and nights, finally slaying him on the tenth day, celebrated as Vijayadashami or Dussehra. During Navaratri, the nine forms of Durga (Navadurga) are worshipped, each representing different aspects of the Divine Feminine. The festival is celebrated differently across India - with Durga Puja in Bengal, Garba and Dandiya in Gujarat, and Bommai Kolu in South India.',
    detailedRituals: [
      {
        title: 'Nine Forms of Durga Worship',
        description: 'Each day of Navaratri is dedicated to one of the nine forms of Goddess Durga: Shailaputri, Brahmacharini, Chandraghanta, Kushmanda, Skandamata, Katyayani, Kalaratri, Mahagauri, and Siddhidatri. Specific colors and offerings are associated with each day.'
      },
      {
        title: 'Fasting',
        description: 'Devotees observe fasts during the nine days, consuming only fruits, milk, and specific fasting foods. The fast is a form of purification and devotion, helping devotees focus on spiritual practices.'
      },
      {
        title: 'Garba and Dandiya',
        description: 'In Gujarat and other western states, devotees perform traditional Garba and Dandiya dances in circles around a lamp or idol of the Goddess, celebrating her divine energy with music, dance, and colorful attire.'
      },
      {
        title: 'Kanya Puja',
        description: 'On the eighth or ninth day, young girls (representing the nine forms of Durga) are worshipped, their feet are washed, and they are offered food and gifts. This ritual honors the divine feminine energy present in all women.'
      }
    ],
    spiritualBenefits: [
      'Victory over inner demons and negativity',
      'Empowerment and strength',
      'Spiritual purification',
      'Blessings of the Divine Mother',
      'Removal of obstacles',
      'Prosperity and abundance',
      'Protection from evil forces'
    ],
    pujaItems: [
      'Durga Idol or Picture',
      'Red Flowers and Garlands',
      'Coconut',
      'Betel Leaves and Nuts',
      'Fruits and Sweets',
      'Incense and Camphor',
      'Kumkum and Turmeric',
      'Sandalwood Paste',
      'Kalash (Sacred Pot)',
      'Diya and Oil',
      'Durva Grass',
      'Fasting Foods'
    ],
    mantras: [
      {
        title: 'Durga Mantra',
        sanskrit: 'ॐ दुं दुर्गायै नमः',
        meaning: 'Salutations to Goddess Durga. This powerful beej mantra invokes the protective and empowering energy of the Divine Mother.'
      },
      {
        title: 'Durga Gayatri Mantra',
        sanskrit: 'ॐ कात्यायन्यै विद्महे कन्याकुमारी धीमहि तन्नो दुर्गिः प्रचोदयात्',
        meaning: 'We meditate on Goddess Katyayani, the maiden form of Durga. May the Goddess illuminate our intellect and grant us strength to overcome all obstacles.'
      }
    ],
    bestTime: 'Morning and Evening',
    celebratedIn: 'All over India',
    deity: 'Goddess Durga (Nine Forms)'
  },
  {
    id: 'dussehra',
    name: 'Dussehra',
    date: '12',
    month: 'October 2025',
    description: 'Celebrates the victory of Lord Rama over Ravana and good over evil.',
    significance: 'Marks the end of Navaratri and the triumph of righteousness.',
    rituals: ['Ravana Dahan', 'Durga Visarjan', 'Ram Leela', 'Weapon Worship'],
    image: 'https://readdy.ai/api/search-image?query=dramatic%20Ravana%20effigy%20burning%20ceremony%20at%20dusk%2C%20large%20demon%20figure%20with%20ten%20heads%2C%20festive%20crowd%20celebration%2C%20fireworks%2C%20traditional%20Dussehra%20celebration%2C%20victory%20of%20good%20over%20evil%20theme%2C%20cultural%20festival%20atmosphere%2C%20evening%20lighting&width=600&height=400&seq=fest011&orientation=landscape',
    category: 'major',
    detailedStory: 'Dussehra, also known as Vijayadashami, marks the culmination of Navaratri and celebrates two significant victories - Goddess Durga\'s triumph over the demon Mahishasura and Lord Rama\'s victory over the ten-headed demon king Ravana. The festival symbolizes the eternal victory of good over evil, truth over falsehood, and dharma over adharma. According to the Ramayana, after Ravana abducted Sita and took her to Lanka, Rama, along with his brother Lakshmana and the monkey army led by Hanuman, waged war against Ravana. The battle lasted for ten days, and on the tenth day, Rama killed Ravana with a divine arrow, rescuing Sita and restoring dharma. In many parts of India, especially North India, effigies of Ravana, his brother Kumbhakarna, and son Meghanada are burned in grand celebrations called Ravana Dahan, accompanied by fireworks and festivities. In Bengal and other eastern states, the day marks the immersion of Durga idols (Durga Visarjan) after nine days of worship. Dussehra is also considered an auspicious day to begin new ventures, start learning, and worship tools and weapons.',
    detailedRituals: [
      {
        title: 'Ravana Dahan',
        description: 'Large effigies of Ravana, Kumbhakarna, and Meghanada are erected in open grounds. In the evening, these effigies are set on fire with arrows, symbolizing the destruction of evil. The event is accompanied by fireworks, music, and celebrations.'
      },
      {
        title: 'Ram Leela',
        description: 'In the days leading up to Dussehra, dramatic enactments of the Ramayana called Ram Leela are performed in various parts of India. These performances depict the life of Rama, culminating in the victory over Ravana on Dussehra.'
      },
      {
        title: 'Durga Visarjan',
        description: 'In Bengal, Odisha, and other eastern states, the festival marks the end of Durga Puja. Devotees bid farewell to Goddess Durga by immersing her idols in rivers or water bodies with great emotion and celebration.'
      },
      {
        title: 'Shami Puja and Weapon Worship',
        description: 'The Shami tree is worshipped as it is believed that the Pandavas hid their weapons in this tree during their exile. People exchange Shami leaves as a symbol of good luck. Tools, vehicles, and weapons are also worshipped on this day.'
      }
    ],
    spiritualBenefits: [
      'Victory over inner demons and vices',
      'Triumph of righteousness',
      'Removal of negativity',
      'Courage and strength',
      'Success in new beginnings',
      'Protection from evil',
      'Spiritual empowerment'
    ],
    pujaItems: [
      'Rama Idol or Picture',
      'Shami Tree Leaves',
      'Flowers and Garlands',
      'Fruits and Sweets',
      'Incense and Camphor',
      'Kumkum and Turmeric',
      'Coconut',
      'Betel Leaves and Nuts',
      'Weapons or Tools (for worship)',
      'Diya and Oil',
      'Sandalwood Paste',
      'Rangoli Colors'
    ],
    mantras: [
      {
        title: 'Rama Vijaya Mantra',
        sanskrit: 'श्री राम जय राम जय जय राम',
        meaning: 'Victory to Lord Rama. This mantra celebrates Rama\'s victory and invokes his blessings for triumph over obstacles and evil.'
      },
      {
        title: 'Durga Vijaya Mantra',
        sanskrit: 'ॐ जयन्ती मङ्गला काली भद्रकाली कपालिनी। दुर्गा क्षमा शिवा धात्री स्वाहा स्वधा नमोऽस्तु ते॥',
        meaning: 'Salutations to the victorious Goddess in her various forms - Jayanti, Mangala, Kali, Bhadrakali, Kapalini, Durga, Kshama, Shiva, Dhatri. We bow to you with offerings.'
      }
    ],
    bestTime: 'Evening (for Ravana Dahan)',
    celebratedIn: 'All over India',
    deity: 'Lord Rama and Goddess Durga'
  },
  {
    id: 'diwali',
    name: 'Diwali',
    date: '1',
    month: 'November 2025',
    description: 'Festival of lights celebrating the return of Lord Rama to Ayodhya.',
    significance: 'Symbolizes the victory of light over darkness and good over evil.',
    rituals: ['Lakshmi Puja', 'Lighting Diyas', 'Fireworks', 'Rangoli'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Diwali%20celebration%20with%20rows%20of%20glowing%20clay%20diyas%2C%20colorful%20rangoli%20patterns%2C%20marigold%20flowers%2C%20brass%20lamps%2C%20festive%20decorations%2C%20warm%20golden%20lighting%2C%20joyful%20atmosphere%2C%20traditional%20Indian%20festival%20of%20lights%2C%20spiritual%20illumination&width=600&height=400&seq=fest012&orientation=landscape',
    category: 'major',
    detailedStory: 'Diwali, the festival of lights, is one of the most celebrated festivals in Hinduism, symbolizing the victory of light over darkness, good over evil, and knowledge over ignorance. The festival has multiple mythological origins. The most popular legend celebrates the return of Lord Rama to Ayodhya after 14 years of exile and his victory over the demon king Ravana. The people of Ayodhya lit oil lamps to welcome their beloved king, illuminating the entire kingdom. Another legend associates Diwali with Goddess Lakshmi, the goddess of wealth and prosperity, who emerged from the ocean during Samudra Manthan on this day. It is also believed that Lord Krishna killed the demon Narakasura on this day, freeing 16,000 captive princesses. In Jainism, Diwali marks the nirvana of Lord Mahavira, and in Sikhism, it celebrates the release of Guru Hargobind Ji from imprisonment. Diwali is celebrated over five days, with the main day dedicated to Lakshmi Puja. Homes are cleaned, decorated with rangoli and lights, and families gather to worship, exchange gifts, and enjoy festive meals.',
    detailedRituals: [
      {
        title: 'Lakshmi Puja',
        description: 'The main ritual of Diwali is the worship of Goddess Lakshmi and Lord Ganesha. Devotees clean their homes, create a sacred space, and perform puja with flowers, sweets, and offerings. The puja is performed in the evening when Lakshmi is believed to visit homes.'
      },
      {
        title: 'Lighting Diyas and Lamps',
        description: 'Clay oil lamps (diyas) are lit and placed around the home, in windows, doorways, and courtyards. The lights symbolize the removal of spiritual darkness and the welcoming of prosperity and positive energy.'
      },
      {
        title: 'Rangoli Decoration',
        description: 'Colorful rangoli patterns are created at the entrance of homes using colored powders, flowers, and rice. These beautiful designs welcome Goddess Lakshmi and add to the festive atmosphere.'
      },
      {
        title: 'Fireworks and Celebrations',
        description: 'Fireworks and crackers are burst to celebrate the joyous occasion. Families exchange gifts, sweets, and good wishes. New clothes are worn, and special festive meals are prepared and shared.'
      }
    ],
    spiritualBenefits: [
      'Blessings of wealth and prosperity',
      'Removal of darkness and ignorance',
      'Victory over negative forces',
      'Spiritual illumination',
      'Family harmony and joy',
      'New beginnings and fresh starts',
      'Divine grace of Lakshmi'
    ],
    pujaItems: [
      'Lakshmi and Ganesha Idols',
      'Clay Diyas and Oil',
      'Rangoli Colors',
      'Flowers and Garlands',
      'Fruits and Sweets',
      'Incense and Camphor',
      'Kumkum and Turmeric',
      'Coconut',
      'Betel Leaves and Nuts',
      'Silver or Gold Coin',
      'New Account Books',
      'Sandalwood Paste'
    ],
    mantras: [
      {
        title: 'Lakshmi Mantra',
        sanskrit: 'ॐ श्रीं ह्रीं श्रीं कमले कमलालये प्रसीद प्रसीद ॐ श्रीं ह्रीं श्रीं महालक्ष्म्यै नमः',
        meaning: 'I bow to Goddess Mahalakshmi who resides in the lotus. May she be pleased and bestow her blessings of prosperity, abundance, and spiritual wealth upon us.'
      },
      {
        title: 'Diwali Prayer',
        sanskrit: 'दीपज्योति परब्रह्म दीपज्योति जनार्दनः। दीपो हरतु मे पापं दीपज्योतिर्नमोऽस्तु ते॥',
        meaning: 'The light of the lamp is the Supreme Brahman, the light is Janardana (Vishnu). May the lamp remove my sins. I bow to the divine light.'
      }
    ],
    bestTime: 'Evening (Pradosh Kaal)',
    celebratedIn: 'All over India and worldwide',
    deity: 'Goddess Lakshmi and Lord Ganesha'
  },
  {
    id: 'pitru-paksha',
    name: 'Pitru Paksha',
    date: '18',
    month: 'September 2025',
    description: 'Fortnight dedicated to honoring ancestors through Shradh rituals and offerings.',
    significance: 'A sacred period to pay homage to departed ancestors and seek their blessings for family prosperity.',
    rituals: ['Shradh Puja', 'Pinda Daan', 'Tarpan', 'Feeding Brahmins'],
    image: 'https://readdy.ai/api/search-image?query=traditional%20Hindu%20Shradh%20ceremony%20setup%20with%20brass%20vessels%2C%20black%20sesame%20seeds%2C%20rice%20balls%20pinda%2C%20kusha%20grass%2C%20sacred%20water%2C%20ancestral%20worship%20items%2C%20peaceful%20spiritual%20atmosphere%2C%20warm%20lighting%2C%20devotional%20setting&width=600&height=400&seq=fest013&orientation=landscape',
    category: 'spiritual',
    detailedStory: 'Pitru Paksha is a 16-day period in the Hindu calendar dedicated to honoring deceased ancestors. According to Hindu belief, during this period, the souls of ancestors descend to the earthly realm to receive offerings from their descendants. The legend of Karna from the Mahabharata explains the origin - when Karna died and reached heaven, he found only gold and jewels as food because he had donated only wealth during his lifetime, never food. He was sent back to earth for 15 days to make food offerings, which became Pitru Paksha. Performing Shradh rituals during this period is believed to help ancestors attain moksha and bring blessings of prosperity, health, and happiness to the family.',
    detailedRituals: [
      {
        title: 'Tarpan',
        description: 'Offering water mixed with black sesame seeds, barley, and kusha grass to ancestors while reciting their names and gotras. This is performed facing south, the direction of Yama, the god of death.'
      },
      {
        title: 'Pinda Daan',
        description: 'Offering rice balls (pindas) representing the bodies of ancestors. These are placed on kusha grass and offered with prayers for the peace and liberation of departed souls.'
      },
      {
        title: 'Shradh Puja',
        description: 'A formal ceremony performed by a priest, invoking ancestors and offering food, water, and prayers. The ritual includes specific mantras and offerings based on family traditions.'
      },
      {
        title: 'Brahmin Bhojan',
        description: 'Feeding Brahmins is considered highly meritorious during Pitru Paksha. The food offered to Brahmins is believed to reach the ancestors directly.'
      }
    ],
    spiritualBenefits: [
      'Peace for departed ancestors',
      'Blessings for family prosperity',
      'Removal of ancestral debts (Pitru Rin)',
      'Protection from ancestral curses',
      'Harmony in family relationships',
      'Good health and longevity',
      'Success in endeavors'
    ],
    pujaItems: [
      'Black Sesame Seeds',
      'Rice for Pinda',
      'Kusha Grass',
      'Gangajal',
      'Barley',
      'White Flowers',
      'Incense and Camphor',
      'Ghee',
      'White Cloth',
      'Copper Vessel',
      'Diya and Oil',
      'Food for Offering'
    ],
    mantras: [
      {
        title: 'Pitru Tarpan Mantra',
        sanskrit: 'ॐ पितृभ्यः स्वधा नमः',
        meaning: 'Salutations to the ancestors with offerings. This mantra is chanted while offering water and sesame seeds to departed souls.'
      },
      {
        title: 'Pitru Gayatri Mantra',
        sanskrit: 'ॐ पितृगणाय विद्महे जगत्धारिणे धीमहि तन्नो पितरः प्रचोदयात्',
        meaning: 'We meditate on the ancestors who support the world. May they illuminate our intellect and guide us with their blessings.'
      }
    ],
    bestTime: 'Morning (before noon)',
    celebratedIn: 'All over India, especially Gaya',
    deity: 'Ancestors (Pitru Devata)'
  },
  {
    id: 'karwa-chauth',
    name: 'Karwa Chauth',
    date: '21',
    month: 'October 2025',
    description: 'A day-long fast observed by married women for the longevity and well-being of their husbands.',
    significance: 'Celebrates the sacred bond of marriage and a wife\'s devotion to her husband.',
    rituals: ['Fasting', 'Moon Worship', 'Sargi', 'Breaking Fast'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Karwa%20Chauth%20celebration%20with%20decorated%20thali%2C%20karwa%20pot%2C%20sieve%20for%20moon%20viewing%2C%20henna%20decorated%20hands%2C%20traditional%20jewelry%2C%20evening%20moonlight%2C%20romantic%20devotional%20atmosphere%2C%20warm%20lighting%2C%20Indian%20married%20woman%20celebration&width=600&height=400&seq=fest014&orientation=landscape',
    category: 'family',
    detailedStory: 'Karwa Chauth is a significant festival celebrated by married Hindu women, primarily in North India. The word "Karwa" refers to an earthen pot and "Chauth" means the fourth day. The festival falls on the fourth day after the full moon in the month of Kartik. According to legend, Queen Veervati observed a strict fast for her husband but broke it before moonrise due to her brothers\' trickery, causing her husband to fall ill. Through her devotion and prayers to Lord Shiva and Goddess Parvati, she was able to revive him. Another legend tells of Savitri who brought her husband Satyavan back from death through her devotion. Women observe this fast from sunrise to moonrise, praying for their husband\'s long life, prosperity, and well-being.',
    detailedRituals: [
      {
        title: 'Sargi',
        description: 'Before sunrise, the mother-in-law gives sargi (pre-dawn meal) to the daughter-in-law. This includes fruits, sweets, and savory items that provide energy throughout the day of fasting.'
      },
      {
        title: 'Day-long Fast',
        description: 'Married women observe a strict nirjala (without water) fast from sunrise to moonrise. They spend the day in prayers, applying mehndi, and preparing for the evening puja.'
      },
      {
        title: 'Evening Puja',
        description: 'Women gather in groups to perform the Karwa Chauth puja, listening to the vrat katha (story), and passing the karwa (earthen pot) among themselves while singing traditional songs.'
      },
      {
        title: 'Moon Worship and Breaking Fast',
        description: 'After moonrise, women view the moon through a sieve, then look at their husband\'s face through the same sieve. The husband then offers water and the first morsel of food to break the fast.'
      }
    ],
    spiritualBenefits: [
      'Long life and prosperity for husband',
      'Strengthening of marital bond',
      'Blessings of Goddess Parvati',
      'Family harmony and happiness',
      'Fulfillment of wishes',
      'Protection of husband from harm',
      'Spiritual merit for the family'
    ],
    pujaItems: [
      'Karwa (Earthen Pot)',
      'Sieve (Chalni)',
      'Diya and Oil',
      'Mehndi',
      'Sindoor',
      'Bangles',
      'Sweets',
      'Fruits',
      'Flowers',
      'Kumkum and Turmeric',
      'Puja Thali',
      'Karwa Chauth Katha Book'
    ],
    mantras: [
      {
        title: 'Karwa Chauth Mantra',
        sanskrit: 'ॐ जय शिव-पार्वती, जय शिव-पार्वती',
        meaning: 'Victory to Lord Shiva and Goddess Parvati. This mantra is chanted during the puja to invoke the blessings of the divine couple for marital bliss.'
      },
      {
        title: 'Chandra Mantra',
        sanskrit: 'ॐ सों सोमाय नमः',
        meaning: 'Salutations to the Moon God. This mantra is chanted while viewing the moon to seek blessings for the husband\'s longevity.'
      }
    ],
    bestTime: 'Evening (Moonrise)',
    celebratedIn: 'North India',
    deity: 'Lord Shiva, Goddess Parvati, and Moon'
  },
  {
    id: 'chhath-puja',
    name: 'Chhath Puja',
    date: '7-8',
    month: 'November 2025',
    description: 'Ancient Hindu festival dedicated to the Sun God and Chhathi Maiya for prosperity and well-being.',
    significance: 'One of the most eco-friendly festivals, celebrating gratitude to the Sun for sustaining life on Earth.',
    rituals: ['Nahay Khay', 'Kharna', 'Sandhya Arghya', 'Usha Arghya'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Chhath%20Puja%20celebration%20at%20river%20ghat%20during%20sunset%2C%20devotees%20standing%20in%20water%20offering%20arghya%20to%20sun%2C%20traditional%20bamboo%20soop%20with%20fruits%20and%20thekua%2C%20orange%20golden%20sky%20reflection%2C%20spiritual%20devotion%2C%20Bihar%20festival%20atmosphere&width=600&height=400&seq=fest015&orientation=landscape',
    category: 'major',
    detailedStory: 'Chhath Puja is an ancient Hindu Vedic festival dedicated to Surya (Sun God) and Chhathi Maiya (Goddess Usha). It is believed to be one of the oldest forms of sun worship, dating back to the Vedic period. The festival is celebrated mainly in Bihar, Jharkhand, and eastern Uttar Pradesh. According to mythology, Chhath was first performed by Draupadi during the Pandavas\' exile, and later by Karna, the son of Surya. The festival involves rigorous rituals over four days, including fasting, holy bathing, and offering prayers to the setting and rising sun. It is unique because it worships the setting sun, symbolizing gratitude for the life-giving energy, and the rising sun, symbolizing hope and new beginnings.',
    detailedRituals: [
      {
        title: 'Nahay Khay (Day 1)',
        description: 'Devotees take a holy bath in a river or pond and prepare a vegetarian meal. The house is cleaned thoroughly, and the devotee eats only one meal prepared with strict purity.'
      },
      {
        title: 'Kharna (Day 2)',
        description: 'Devotees observe a day-long fast without water, breaking it in the evening after sunset puja. Kheer (rice pudding) and roti are prepared and offered to the deity before consumption.'
      },
      {
        title: 'Sandhya Arghya (Day 3)',
        description: 'The main day when devotees go to riverbanks or water bodies to offer arghya (water offering) to the setting sun. They stand in water and offer fruits, thekua, and other items in bamboo baskets.'
      },
      {
        title: 'Usha Arghya (Day 4)',
        description: 'Before sunrise, devotees return to the water body to offer arghya to the rising sun. After this final offering, the fast is broken, and prasad is distributed to family and community.'
      }
    ],
    spiritualBenefits: [
      'Blessings of Sun God for health and vitality',
      'Prosperity and abundance',
      'Fulfillment of wishes',
      'Protection from diseases',
      'Family well-being',
      'Spiritual purification',
      'Environmental consciousness'
    ],
    pujaItems: [
      'Bamboo Baskets (Soop and Daura)',
      'Sugarcane',
      'Coconut',
      'Bananas',
      'Seasonal Fruits',
      'Thekua (Traditional Sweet)',
      'Rice',
      'Turmeric Plant',
      'Ginger',
      'Radish',
      'Diya and Oil',
      'New Clothes'
    ],
    mantras: [
      {
        title: 'Surya Mantra',
        sanskrit: 'ॐ सूर्याय नमः',
        meaning: 'Salutations to the Sun God. This mantra is chanted while offering arghya to invoke the blessings of Surya for health, prosperity, and spiritual enlightenment.'
      },
      {
        title: 'Chhathi Maiya Mantra',
        sanskrit: 'जय छठी मैया, जय छठी मैया',
        meaning: 'Victory to Chhathi Maiya. This mantra praises the goddess who is believed to be the sister of the Sun God and protector of children.'
      }
    ],
    bestTime: 'Sunrise and Sunset',
    celebratedIn: 'Bihar, Jharkhand, Eastern UP, Nepal',
    deity: 'Sun God (Surya) and Chhathi Maiya'
  },
  {
    id: 'tulsi-vivah',
    name: 'Tulsi Vivah',
    date: '12',
    month: 'November 2025',
    description: 'Ceremonial marriage of the Tulsi plant to Lord Vishnu, marking the beginning of the wedding season.',
    significance: 'Celebrates the sacred union and marks the end of Chaturmas, opening the Hindu wedding season.',
    rituals: ['Tulsi Puja', 'Vivah Ceremony', 'Aarti', 'Prasad Distribution'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Tulsi%20Vivah%20ceremony%20with%20decorated%20Tulsi%20plant%20in%20pot%2C%20small%20Vishnu%20idol%2C%20wedding%20decorations%2C%20marigold%20flowers%2C%20mango%20leaves%2C%20traditional%20Hindu%20wedding%20setup%2C%20evening%20diya%20lighting%2C%20spiritual%20celebration&width=600&height=400&seq=fest016&orientation=landscape',
    category: 'auspicious',
    detailedStory: 'Tulsi Vivah is the ceremonial marriage of the Tulsi (holy basil) plant to Lord Vishnu or his avatar Krishna. According to Hindu mythology, Tulsi was originally a woman named Vrinda who was devoted to Lord Vishnu. After a series of events involving her husband, the demon king Jalandhar, she was transformed into the Tulsi plant. Lord Vishnu promised to marry her annually, and this ceremony is observed as Tulsi Vivah. The festival marks the end of the four-month period of Chaturmas when marriages are prohibited. After Tulsi Vivah, the Hindu wedding season officially begins. The ceremony is performed with all the rituals of a traditional Hindu wedding, with the Tulsi plant as the bride and a Shaligram stone or Vishnu idol as the groom.',
    detailedRituals: [
      {
        title: 'Decoration of Tulsi',
        description: 'The Tulsi plant is decorated like a bride with a red cloth, jewelry, and flowers. A mandap (canopy) is created around the plant using sugarcane sticks and mango leaves.'
      },
      {
        title: 'Vivah Ceremony',
        description: 'The marriage ceremony is performed with Vedic mantras, just like a traditional Hindu wedding. The Shaligram or Vishnu idol is placed next to Tulsi, and the priest conducts the rituals.'
      },
      {
        title: 'Phere and Sindoor',
        description: 'Seven rounds (phere) are symbolically performed around the sacred fire. Sindoor is applied to the Tulsi plant, and the marriage is solemnized with the tying of the sacred thread.'
      },
      {
        title: 'Aarti and Prasad',
        description: 'After the ceremony, aarti is performed, and prasad including sweets, fruits, and sugarcane is distributed to all present. The celebration continues with bhajans and community feasting.'
      }
    ],
    spiritualBenefits: [
      'Blessings for marriage and relationships',
      'Prosperity and happiness in married life',
      'Removal of obstacles in finding a spouse',
      'Spiritual merit equivalent to performing a wedding',
      'Blessings of Lord Vishnu',
      'Purification of the home',
      'Good fortune for the family'
    ],
    pujaItems: [
      'Tulsi Plant',
      'Shaligram or Vishnu Idol',
      'Red Cloth for Tulsi',
      'Sugarcane Sticks',
      'Mango Leaves',
      'Marigold Flowers',
      'Coconut',
      'Betel Leaves and Nuts',
      'Sindoor',
      'Sacred Thread',
      'Sweets',
      'Diya and Oil'
    ],
    mantras: [
      {
        title: 'Tulsi Mantra',
        sanskrit: 'ॐ तुलस्यै नमः',
        meaning: 'Salutations to Tulsi. This mantra honors the sacred Tulsi plant and invokes her blessings for purity and devotion.'
      },
      {
        title: 'Vishnu Mantra',
        sanskrit: 'ॐ नमो भगवते वासुदेवाय',
        meaning: 'Salutations to Lord Vasudeva (Vishnu). This mantra is chanted during the ceremony to invoke Lord Vishnu\'s presence and blessings.'
      }
    ],
    bestTime: 'Evening',
    celebratedIn: 'All over India',
    deity: 'Tulsi and Lord Vishnu'
  },
  {
    id: 'makar-sankranti',
    name: 'Makar Sankranti',
    date: '14',
    month: 'January 2026',
    description: 'Harvest festival marking the sun\'s transition into Capricorn and the beginning of longer days.',
    significance: 'Celebrates the harvest season and the sun\'s northward journey (Uttarayan).',
    rituals: ['Holy Bath', 'Surya Puja', 'Kite Flying', 'Til-Gur Distribution'],
    image: 'https://readdy.ai/api/search-image?query=vibrant%20Makar%20Sankranti%20celebration%20with%20colorful%20kites%20in%20blue%20sky%2C%20traditional%20til%20gur%20laddoos%2C%20sesame%20sweets%2C%20harvest%20festival%20atmosphere%2C%20joyful%20celebration%2C%20warm%20winter%20sunlight%2C%20Indian%20festival%20of%20harvest&width=600&height=400&seq=fest017&orientation=landscape',
    category: 'major',
    detailedStory: 'Makar Sankranti is one of the few Hindu festivals based on the solar calendar rather than the lunar calendar. It marks the day when the sun enters the zodiac sign of Capricorn (Makar) and begins its northward journey (Uttarayan). This transition is considered highly auspicious as it marks the end of the winter solstice and the beginning of longer, warmer days. The festival is celebrated across India with different names - Pongal in Tamil Nadu, Lohri in Punjab, Bihu in Assam, and Uttarayan in Gujarat. It is primarily a harvest festival, celebrating the first harvest of the season. The day is considered so auspicious that it is believed that those who die during Uttarayan attain moksha, as mentioned in the Bhagavad Gita when Bhishma chose to die during this period.',
    detailedRituals: [
      {
        title: 'Holy Bath',
        description: 'Taking a holy dip in sacred rivers like Ganga, Yamuna, or Godavari is considered highly meritorious. Millions gather at pilgrimage sites for this ritual bath at sunrise.'
      },
      {
        title: 'Surya Puja',
        description: 'Worship of the Sun God is performed with offerings of water, red flowers, and red sandalwood. Surya Namaskar (sun salutation) is practiced, and the Gayatri Mantra is chanted.'
      },
      {
        title: 'Til-Gur Exchange',
        description: 'Sesame seeds (til) and jaggery (gur) are exchanged with the greeting "Til-gur ghya, god god bola" (Take til-gur and speak sweet words). These foods are believed to keep the body warm.'
      },
      {
        title: 'Kite Flying',
        description: 'In Gujarat and other regions, kite flying is a major tradition. The sky fills with colorful kites as people celebrate the festival with joy and friendly competition.'
      }
    ],
    spiritualBenefits: [
      'Blessings of the Sun God',
      'Spiritual purification',
      'Good health and vitality',
      'Prosperity and abundance',
      'Removal of past sins',
      'Auspicious new beginnings',
      'Harmony and goodwill'
    ],
    pujaItems: [
      'Sesame Seeds (Til)',
      'Jaggery (Gur)',
      'Rice',
      'Sugarcane',
      'Peanuts',
      'Coconut',
      'Flowers',
      'Incense and Camphor',
      'Diya and Oil',
      'Kites and String',
      'New Clothes',
      'Donation Items'
    ],
    mantras: [
      {
        title: 'Surya Gayatri Mantra',
        sanskrit: 'ॐ भास्कराय विद्महे महाद्युतिकराय धीमहि तन्नो आदित्यः प्रचोदयात्',
        meaning: 'We meditate on the radiant Sun God, the source of great light. May Aditya illuminate our intellect and guide us toward enlightenment.'
      },
      {
        title: 'Sankranti Mantra',
        sanskrit: 'ॐ सूर्याय नमः, ॐ आदित्याय नमः',
        meaning: 'Salutations to the Sun, salutations to Aditya. These mantras honor the Sun God on his auspicious transition into Uttarayan.'
      }
    ],
    bestTime: 'Sunrise',
    celebratedIn: 'All over India',
    deity: 'Sun God (Surya)'
  },
  {
    id: 'vasant-panchami',
    name: 'Vasant Panchami',
    date: '2',
    month: 'February 2026',
    description: 'Festival celebrating the arrival of spring and honoring Goddess Saraswati.',
    significance: 'Marks the beginning of spring and is dedicated to Saraswati, the goddess of knowledge and arts.',
    rituals: ['Saraswati Puja', 'Wearing Yellow', 'Flying Kites', 'Starting Education'],
    image: 'https://readdy.ai/api/search-image?query=beautiful%20Vasant%20Panchami%20celebration%20with%20Goddess%20Saraswati%20idol%2C%20yellow%20flowers%2C%20books%20and%20musical%20instruments%2C%20spring%20atmosphere%2C%20students%20worshipping%2C%20warm%20golden%20lighting%2C%20educational%20devotional%20setting&width=600&height=400&seq=fest018&orientation=landscape',
    category: 'spiritual',
    detailedStory: 'Vasant Panchami, also known as Saraswati Puja, marks the arrival of spring (Vasant) and is dedicated to Goddess Saraswati, the deity of knowledge, music, arts, and learning. According to Hindu mythology, on this day, Lord Brahma created Goddess Saraswati to bring knowledge and wisdom to the world. The festival falls on the fifth day (Panchami) of the bright fortnight of the month of Magha. Yellow is the predominant color of the festival, symbolizing the mustard fields that bloom during spring and the vibrancy of life. It is considered an auspicious day to begin education, and many children are initiated into learning on this day through a ceremony called Vidyarambham. Students worship their books, musical instruments, and writing materials, seeking Saraswati\'s blessings for success in their studies and creative pursuits.',
    detailedRituals: [
      {
        title: 'Saraswati Puja',
        description: 'Goddess Saraswati is worshipped with white and yellow flowers, fruits, and sweets. Books, musical instruments, and writing materials are placed before the deity for blessings.'
      },
      {
        title: 'Wearing Yellow',
        description: 'Devotees wear yellow clothes and eat yellow-colored foods like saffron rice and yellow sweets. Yellow represents the blooming mustard fields and the energy of spring.'
      },
      {
        title: 'Vidyarambham',
        description: 'Young children are initiated into learning on this auspicious day. They are taught to write their first letters, usually "Om" or the first letter of their name, on rice grains.'
      },
      {
        title: 'Cultural Programs',
        description: 'Schools and colleges organize cultural programs, music performances, and art exhibitions. Students participate in competitions and showcase their talents.'
      }
    ],
    spiritualBenefits: [
      'Blessings for knowledge and wisdom',
      'Success in education and learning',
      'Enhancement of creative abilities',
      'Clarity of thought and speech',
      'Removal of ignorance',
      'Artistic inspiration',
      'Academic excellence'
    ],
    pujaItems: [
      'Saraswati Idol or Picture',
      'Yellow Flowers',
      'White Flowers',
      'Books and Notebooks',
      'Pen and Ink',
      'Musical Instruments',
      'Yellow Sweets',
      'Fruits',
      'Incense and Camphor',
      'Yellow Cloth',
      'Diya and Oil',
      'Rice Grains'
    ],
    mantras: [
      {
        title: 'Saraswati Mantra',
        sanskrit: 'ॐ ऐं सरस्वत्यै नमः',
        meaning: 'Salutations to Goddess Saraswati. This beej mantra invokes the goddess\'s blessings for knowledge, wisdom, and creative abilities.'
      },
      {
        title: 'Saraswati Vandana',
        sanskrit: 'या कुन्देन्दुतुषारहारधवला या शुभ्रवस्त्रावृता',
        meaning: 'She who is as white as the jasmine flower, the moon, and snow, who wears white garments - this verse praises Saraswati\'s purity and grace.'
      }
    ],
    bestTime: 'Morning',
    celebratedIn: 'All over India, especially Bengal and North India',
    deity: 'Goddess Saraswati'
  }
];
